import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import CustomFieldsManager from '../components/CustomFieldsManager';
import SLAManager from '../components/SLAManager';
import SLAProfiles from '../components/SLAProfiles';
import AppRules from '../components/AppRules';
import Workflows from '../components/Workflows';
import AssetSettings from '../components/AssetSettings';
import CalendarFeeds from '../components/CalendarFeeds';
import TodoEmailSettings from '../components/TodoEmailSettings';
import FeatureSwitches from '../components/FeatureSwitches';
import DocsSettings from '../components/docs/DocsSettings';
import UsersPage from './settings/UsersPage';
import PeopleSettingsPage from './settings/PeopleSettingsPage';
import ContractorsPage from './settings/ContractorsPage';
import { DepartmentsPage, TicketCategoriesPage, PrioritiesStatusesPage } from './settings/ListPages';
import { OverviewPage, PrivacyPage, RecordsPage } from './settings/SystemPages';
import { PersonalDataPage, RetentionPage, ProcessingPage } from '../components/gdpr/GdprSettings';
import { SettingsContext } from './settings/SettingsContext';
import Icon from './settings/icons';
import {
  GROUPS, DEFAULT_PATH, findPage, pathOf, legacyTarget, searchSettings
} from './settings/registry';
import './settings/Settings.css';

const API = process.env.REACT_APP_API_URL;

/**
 * SETTINGS: a grouped left-hand nav, a search over every individual setting,
 * and one page at a time (Gordon approved the reorganisation on 26 October).
 *
 * This was a strip of sixteen tabs in build order, one of which (Rules) held
 * nine subjects; ?tab= was read once and never written, so Back, bookmarks
 * and copied links all forgot where you were, and three links in the app
 * landed on Users. Now:
 *
 *   /settings                         the Overview
 *   /settings/:group/:page[/:sub]     a page (Documentation has sub-pages)
 *   ...#anchor                        one setting on it: scrolled to and
 *                                     lit for two seconds
 *   /settings?tab=…[&part=…][#…]      every old address, replaced by its new
 *                                     one (registry.js LEGACY)
 *
 * The nav, the search, the redirects and "On this page" are all built from
 * settings/registry.js. The components are the ones each tab drew before -
 * AppRules, AssetSettings, FeatureSwitches and DocsSettings each draw the
 * part a page names. On a phone the nav is a drawer behind one button.
 *
 * The route is administrators only (App.js); the server enforces every
 * write regardless.
 */

/* ------------------------------------------------------------ the pages */

function TemplatesPointer() {
  return (
    <div className="stn-card">
      <p className="stn-help">
        Ticket templates live in Ticket Mgmt, beside the tickets raised from them. A contractor you
        allow under <Link to="/settings/people/contractors">Contractors</Link> (“May manage ticket
        templates”) sees and edits them from their own portal.
      </p>
      <div className="stn-actions">
        <Link to="/manage/templates" className="btn btn-primary">Open ticket templates</Link>
      </div>
    </div>
  );
}

/** A page's sub-pages, each with its own address (Documentation). */
function SubTabs({ page, sub }) {
  return (
    <nav className="stn-subs" aria-label={`${page.label} sections`}>
      {page.subs.map((s) => (
        <Link key={s.id} to={pathOf(page, { sub: s.id })} aria-current={sub === s.id ? 'page' : undefined}
              className={`stn-sub${sub === s.id ? ' is-on' : ''}`} data-sub={s.id}>
          {s.label}
        </Link>
      ))}
    </nav>
  );
}

const Area = ({ title, children }) => (
  <section className="stn-area" aria-label={title}>
    <h3 className="stn-area-title">{title}</h3>
    {children}
  </section>
);

function renderPage(page, { canEdit, sub, onSub, reloadRules }) {
  const rules = (section) => <AppRules canEdit={canEdit} section={section} onChanged={reloadRules} />;
  switch (`${page.group}/${page.id}`) {
    case 'people/users': return <UsersPage />;
    case 'people/joiners-leavers': return <PeopleSettingsPage />;
    case 'people/departments': return <DepartmentsPage />;
    case 'people/contractors': return <ContractorsPage />;
    case 'people/feature-access': return <FeatureSwitches />;
    case 'people/profile-photos': return rules('photos');
    case 'tickets/ticket-rules': return rules(['approver', 'private-notes', 'digest', 'location']);
    case 'tickets/categories': return <TicketCategoriesPage />;
    case 'tickets/priorities-statuses': return <PrioritiesStatusesPage />;
    case 'tickets/custom-fields': return <CustomFieldsManager />;
    case 'tickets/sla':
      return (
        <>
          <p className="stn-help">
            How each category is doing against these targets is under <Link to="/manage/sla">Ticket Mgmt → SLA Tracking</Link>.
            The hours are working hours: which ones - office hours, 24/7, a client&rsquo;s contract - is
            under <Link to="/settings/tickets/sla-hours">SLA working hours</Link>.
          </p>
          <SLAManager />
        </>
      );
    case 'tickets/sla-hours': return <SLAProfiles />;
    case 'tickets/templates': return <TemplatesPointer />;
    case 'tickets/automation': return <Workflows canEdit={canEdit} />;
    case 'communication/messaging': return rules('messaging');
    case 'communication/groups': return rules('groups');
    case 'communication/calls': return rules('calls');
    case 'communication/recording':
      return (
        <>
          <Area title="Who can use it"><FeatureSwitches only={['recording', 'ai']} /></Area>
          <Area title="How recording works">{rules('recording')}</Area>
        </>
      );
    case 'communication/conversation-review':
      return (
        <>
          {rules('review')}
          <p className="stn-help">Conversations are opened, with a reason, in <Link to="/chat-archive">the Chat archive</Link> (also under Records).</p>
        </>
      );
    case 'communication/meetings':
      return (
        <>
          <FeatureSwitches only={['meetings']} />
          <p className="stn-help">
            Inside Meetings, staff organise, contractors and guests attend the ones they are invited to,
            and the minutes are reviewed in English before they are sent.
          </p>
        </>
      );
    case 'content/links':
      return (
        <>
          <FeatureSwitches only={['links']} />
          <p className="stn-help">The shelf itself is edited in place: open <Link to="/links">Links</Link> and change it there.</p>
        </>
      );
    case 'content/calendars':
      return (
        <>
          <Area title="Who can use To do"><FeatureSwitches only={['todos']} /></Area>
          {rules('todo-workload')}
          <TodoEmailSettings canEdit={canEdit} />
          <CalendarFeeds />
        </>
      );
    case 'content/documentation':
      return (
        <>
          <Area title="Who can open it"><FeatureSwitches only={['docs']} /></Area>
          <Area title="Who can read Help"><FeatureSwitches only={['help']} /></Area>
          <Area title="What each person sees">
            <SubTabs page={page} sub={sub} />
            <DocsSettings sub={sub} onSub={onSub} />
          </Area>
        </>
      );
    case 'system/overview': return <OverviewPage />;
    case 'system/privacy': return <PrivacyPage />;
    case 'system/records': return <RecordsPage />;
    // GDPR tools (122).
    case 'system/personal-data': return <PersonalDataPage />;
    case 'system/retention': return <RetentionPage />;
    case 'system/processing': return <ProcessingPage />;
    case 'assets/enrolment':
      return (
        <>
          <AssetSettings page={page.id} />
          <div className="stn-card" id="s-discovery">
            <h3 className="stn-card-title">Discovery and floor plans</h3>
            <p className="stn-help">
              Devices that never report in — printers, switches, access points, storage — turn up in the
              Discovery inbox, from a scan of an office network or as something plugged into a laptop. The
              switches for both are under <Link to="/settings/assets/device-agent#heartbeat-attached">Device agent</Link>;
              the inbox and the floor plans with their pins are in the Asset Portal, where they are used.
            </p>
            <div className="stn-actions">
              <Link to="/assets/discovery" className="btn btn-secondary">Open the Discovery inbox</Link>
              <Link to="/assets/map" className="btn btn-secondary">Open the floor plans</Link>
            </div>
          </div>
        </>
      );
    default:
      if (page.group === 'assets') return <AssetSettings page={page.id} />;
      return null;
  }
}

/* --------------------------------------------------------------- the nav */

/**
 * Search, then the six groups. The search is a combobox: type to filter (any
 * order of words, accents and apostrophes ignored), arrows to move, Enter to
 * go, Escape to clear. Each result says where it lives and, when the shell
 * knows it, its value now.
 */
function SettingsNav({ current, ctx, onGo, searchRef, idPrefix }) {
  // ?q= - the global search's "See all in Settings" - starts the box with its words.
  const [q, setQ] = useState(() => { try { return new URLSearchParams(window.location.search).get('q') || ''; } catch { return ''; } });
  const [at, setAt] = useState(0);
  const results = useMemo(() => searchSettings(q, ctx), [q, ctx]);
  useEffect(() => { setAt(0); }, [q]);
  const listId = `${idPrefix}-results`;

  const go = (r) => {
    setQ('');
    onGo(r.path);
  };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAt((i) => Math.min(i + 1, Math.max(results.length - 1, 0))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { if (results[at]) { e.preventDefault(); go(results[at]); } }
    else if (e.key === 'Escape' && q) { e.preventDefault(); e.stopPropagation(); setQ(''); }
  };

  return (
    <nav className="stn-nav-inner" aria-label="Settings">
      <div className="stn-search">
        <Icon name="search" className="stn-search-icon" />
        <label htmlFor={`${idPrefix}-q`} className="stn-sr">Search settings</label>
        <input
          id={`${idPrefix}-q`}
          ref={searchRef}
          type="search"
          className="stn-search-input"
          placeholder="Search settings"
          autoComplete="off"
          role="combobox"
          aria-expanded={Boolean(q.trim())}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={q.trim() && results[at] ? `${listId}-${at}` : undefined}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
        />
        <kbd className="stn-kbd" aria-hidden="true">/</kbd>
      </div>

      {q.trim() ? (
        <div className="stn-results-wrap">
          <p className="stn-sr" role="status" aria-live="polite">
            {results.length ? `${results.length} setting${results.length === 1 ? '' : 's'} found` : 'Nothing found'}
          </p>
          <ul className="stn-results" id={listId} role="listbox" aria-label="Settings found">
            {results.length === 0 && (
              <li className="stn-result is-none" role="option" aria-selected="false" aria-disabled="true">
                Nothing called that. Try another word.
              </li>
            )}
            {results.map((r, i) => (
              <li key={r.key} id={`${listId}-${i}`} role="option" aria-selected={i === at}
                  className={`stn-result${i === at ? ' is-at' : ''}`} data-result={r.path}
                  onMouseEnter={() => setAt(i)}
                  onMouseDown={(e) => { e.preventDefault(); go(r); }}>
                <span className="stn-result-label">
                  {r.label}
                  {r.external && <Icon name="external" className="stn-ext" />}
                </span>
                <span className="stn-result-meta">
                  <span className="stn-result-where">{r.where}</span>
                  {r.value && <span className="stn-value">{r.value}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="stn-groups">
          {GROUPS.map((g) => (
            <div key={g.id} className="stn-group" role="group" aria-labelledby={`${idPrefix}-g-${g.id}`}>
              <p className="stn-group-label" id={`${idPrefix}-g-${g.id}`}>{g.label}</p>
              <ul className="stn-links">
                {g.pages.map((p) => {
                  const page = findPage(g.id, p.id);
                  const on = current && current.group === g.id && current.id === p.id;
                  return (
                    <li key={p.id}>
                      <Link
                        to={pathOf(page)}
                        className={`settings-tab stn-link${on ? ' is-active' : ''}`}
                        aria-current={on ? 'page' : undefined}
                        data-page={`${g.id}/${p.id}`}
                        onClick={() => onGo(null)}
                      >
                        <Icon name={p.icon} />
                        <span>{p.label}</span>
                        {p.href && (
                          <>
                            <Icon name="external" className="stn-ext" />
                            <span className="stn-sr"> (in Ticket Mgmt)</span>
                          </>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}

/* ------------------------------------------------------------ the shell */

const NOTICES = {
  rules: (
    <>
      <strong>Rules is split up now.</strong> Ticket rules are here; who can message whom, calls and
      recording are under <Link to="/settings/communication/messaging">Chat, calls &amp; meetings</Link>;
      profile photos under <Link to="/settings/people/profile-photos">People &amp; access</Link>.
    </>
  ),
  unknown: <>That Settings page has moved or does not exist, so here is the Overview. The search finds any setting by name.</>
};

const useNarrow = () => {
  const q = '(max-width: 767px)';
  const [narrow, setNarrow] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : false));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener ? m.addEventListener('change', on) : m.addListener(on);
    return () => (m.removeEventListener ? m.removeEventListener('change', on) : m.removeListener(on));
  }, []);
  return narrow;
};

export default function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const uid = useId().replace(/:/g, '');
  const narrow = useNarrow();
  const [drawer, setDrawer] = useState(false);
  const deskSearch = useRef(null);
  const drawerSearch = useRef(null);
  const toggleRef = useRef(null);
  const titleRef = useRef(null);
  const firstPage = useRef(true);

  /* ---- what every page shares ---- */
  const [me, setMe] = useState(null);
  const [rules, setRules] = useState(null);
  const [features, setFeatures] = useState(null);
  const reloadRules = useCallback(() => axios.get(`${API}/app-settings`)
    .then((r) => setRules(r.data || {})).catch(() => setRules((x) => x || {})), []);
  const reloadFeatures = useCallback(() => axios.get(`${API}/features`)
    .then((r) => setFeatures(r.data || {})).catch(() => setFeatures((x) => x || { failed: true })), []);
  useEffect(() => {
    let live = true;
    axios.get(`${API}/auth/me`).then((r) => { if (live) setMe(r.data); }).catch(() => { if (live) setMe(null); });
    reloadRules();
    reloadFeatures();
    return () => { live = false; };
  }, [reloadRules, reloadFeatures]);
  const ctx = useMemo(() => ({ me, rules, features, reloadRules, reloadFeatures }),
    [me, rules, features, reloadRules, reloadFeatures]);

  /* ---- where we are ---- */
  const parts = String(params['*'] || '').split('/').filter(Boolean);
  const page = parts.length >= 2 ? findPage(parts[0], parts[1]) : null;
  const sub = page && page.subs
    ? (page.subs.some((s) => s.id === parts[2]) ? parts[2] : null)
    : null;

  // Old addresses, missing pages and missing sub-pages: replaced, so Back
  // never returns to an address that only redirects.
  useEffect(() => {
    const legacy = legacyTarget(location.search, location.hash);
    if (legacy) {
      navigate(legacy.to, { replace: true, state: legacy.notice ? { notice: legacy.notice } : undefined });
      return;
    }
    // Keeping ?q= (the global search's "See all in Settings") on the way.
    if (!parts.length) {
      const q = new URLSearchParams(location.search).get('q');
      navigate(q ? `${DEFAULT_PATH}?q=${encodeURIComponent(q)}` : DEFAULT_PATH, { replace: true });
      return;
    }
    if (!page) {
      const group = GROUPS.find((g) => g.id === parts[0]);
      if (group && parts.length === 1) { navigate(pathOf(findPage(group.id, group.pages[0].id)), { replace: true }); return; }
      navigate(DEFAULT_PATH, { replace: true, state: { notice: 'unknown' } });
      return;
    }
    if (page.subs && !sub) navigate(`${pathOf(page)}${location.hash || ''}`, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, location.hash]);

  /* ---- arriving at a setting: scroll to it and light it ---- */
  useEffect(() => {
    const anchor = decodeURIComponent(String(location.hash || '').replace(/^#/, ''));
    // The first page drawn - however it was reached, redirects included -
    // is an arrival, not a move: nothing is focused for it.
    if (!page) return undefined;
    const firstTime = firstPage.current;
    firstPage.current = false;
    if (!anchor) {
      if (!firstTime) {
        window.scrollTo({ top: 0 });
        if (titleRef.current) titleRef.current.focus({ preventScroll: true });
      }
      return undefined;
    }
    // The page draws its parts as their data arrives, so look for a while.
    let tries = 0;
    let timer = null;
    let unlight = null;
    const look = () => {
      const el = document.getElementById(`s-${anchor}`);
      if (el) {
        el.scrollIntoView({ block: 'start', behavior: firstTime ? 'auto' : 'smooth' });
        el.classList.add('stn-lit');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
        unlight = setTimeout(() => el.classList.remove('stn-lit'), 2000);
        return;
      }
      tries += 1;
      if (tries < 40) timer = setTimeout(look, 100);
    };
    look();
    return () => { clearTimeout(timer); clearTimeout(unlight); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash, location.key]);

  /* ---- "/" searches; Escape closes the drawer ---- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const t = e.target;
        const typing = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
        if (typing) return;
        e.preventDefault();
        if (narrow) setDrawer(true);
        else if (deskSearch.current) deskSearch.current.focus();
      } else if (e.key === 'Escape' && drawer) {
        setDrawer(false);
        if (toggleRef.current) toggleRef.current.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [narrow, drawer]);
  useEffect(() => {
    if (drawer && drawerSearch.current) drawerSearch.current.focus();
  }, [drawer]);
  useEffect(() => { if (!narrow) setDrawer(false); }, [narrow]);

  const go = (path) => {
    setDrawer(false);
    if (path) navigate(path);
  };

  const notice = location.state && NOTICES[location.state.notice];
  const canEdit = Boolean(me?.isAdmin);
  // One chip per anchor, named by the first setting at it, in page order.
  const anchors = page ? (page.settings || [])
    .filter((s) => s.anchor && (!s.sub || s.sub === sub))
    .filter((s, i, all) => all.findIndex((x) => x.anchor === s.anchor) === i) : [];

  return (
    <SettingsContext.Provider value={ctx}>
      <div className="container stn">
        <div className="page-header stn-head">
          <h1 className="page-title">Settings</h1>
          <p className="page-description">Firm-wide settings for Jarvis. Your own preferences are in your account menu.</p>
        </div>

        {narrow && (
          <button type="button" ref={toggleRef} className="stn-toggle" aria-expanded={drawer}
                  aria-controls={`${uid}-drawer`} onClick={() => setDrawer(true)}>
            <Icon name="menu" />
            <span className="stn-toggle-text">
              <span className="stn-toggle-group">{page ? page.groupLabel : 'Settings'}</span>
              <span className="stn-toggle-page">{page ? page.label : 'Choose a page'}</span>
            </span>
            <Icon name="search" className="stn-toggle-search" />
          </button>
        )}

        <div className="stn-layout">
          {!narrow && (
            <aside className="stn-nav">
              <SettingsNav current={page} ctx={ctx} onGo={go} searchRef={deskSearch} idPrefix={`${uid}-d`} />
            </aside>
          )}

          <main className="stn-main settings-panel" aria-labelledby={`${uid}-title`}>
            {notice && <div className="stn-banner is-info" role="status">{notice}</div>}
            {page && (
              <>
                <header className="stn-page-head">
                  <p className="stn-crumb">{page.groupLabel}</p>
                  <h2 className="stn-title" id={`${uid}-title`} ref={titleRef} tabIndex={-1}>{page.label}</h2>
                  {page.lead && <p className="stn-lead">{page.lead}</p>}
                </header>
                {anchors.length >= 3 && (
                  <nav className="stn-onpage" aria-label="On this page">
                    <span className="stn-onpage-label">On this page</span>
                    {anchors.map((s) => (
                      <Link key={s.anchor} to={`${location.pathname}#${s.anchor}`} className="stn-onpage-link">
                        {s.label.replace(/: who can (use|open) it$/, '')}
                      </Link>
                    ))}
                  </nav>
                )}
                <div className="stn-content" key={`${page.group}/${page.id}`}>
                  {renderPage(page, {
                    canEdit,
                    sub,
                    onSub: (s) => navigate(pathOf(page, { sub: s })),
                    reloadRules
                  })}
                </div>
              </>
            )}
          </main>
        </div>

        {narrow && drawer && (
          <div className="stn-drawer-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) setDrawer(false); }}>
            <div className="stn-drawer" id={`${uid}-drawer`} role="dialog" aria-modal="true" aria-label="Settings pages">
              <div className="stn-drawer-head">
                <span className="stn-drawer-title">Settings</span>
                <button type="button" className="stn-drawer-close" aria-label="Close"
                        onClick={() => { setDrawer(false); if (toggleRef.current) toggleRef.current.focus(); }}>
                  <Icon name="close" />
                </button>
              </div>
              <SettingsNav current={page} ctx={ctx} onGo={go} searchRef={drawerSearch} idPrefix={`${uid}-m`} />
            </div>
          </div>
        )}
      </div>
    </SettingsContext.Provider>
  );
}
